import { useEffect, useMemo, useRef } from 'react'
import { useActiveChatId, useChatMessages, useLoadMessages, useMessageListScrollActions } from '@renderer/chat'
import { useClaimConversation } from '../../../hooks/use-claim-conversation'
import { useChatScrollTargets } from '../../../views/chat-view/use-chat-scroll-targets'
import { useChatStreamingLifecycle } from '../../../views/chat-view/use-chat-streaming-lifecycle'
import { shouldShowChatMessageListSkeleton } from '../chat-message-list-skeleton'

export interface ConversationChatDrawerModel {
  showScrollToEnd: boolean
  scrollToEnd: ReturnType<typeof useMessageListScrollActions>['scrollToEnd']
  isLoadingMessages: boolean
}

export function useConversationChatDrawer({
  chatId,
  open,
  entranceSettled,
  isFreshSubmit,
  onFreshSubmitSettled,
}: {
  chatId: string
  open: boolean
  entranceSettled: boolean
  isFreshSubmit?: boolean
  onFreshSubmitSettled?: () => void
}): ConversationChatDrawerModel {
  const activeChatId = useActiveChatId()
  const messages = useChatMessages(chatId)

  useClaimConversation(open ? chatId : undefined)

  const { refetch, isFetching, isFetched } = useLoadMessages()
  const refetchAttemptedRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!open || isFetching || activeChatId !== chatId) {
      return
    }
    if (refetchAttemptedRef.current === chatId) {
      return
    }
    refetchAttemptedRef.current = chatId
    void refetch()
  }, [open, isFetching, activeChatId, chatId, refetch])

  useChatStreamingLifecycle({
    chatId,
    resolvedChatId: chatId,
    isFreshSubmit,
    onFreshSubmitSettled,
  })

  const isLoadingMessages = shouldShowChatMessageListSkeleton({
    messageCount: messages.length,
    isFetched,
    entranceSettled,
  })

  const scrollContext = useMemo(() => ({ chatId, instantScrollToBottom: true }), [chatId])
  const scroll = useChatScrollTargets(scrollContext, !isLoadingMessages, messages.length)
  return {
    ...scroll,
    isLoadingMessages,
  }
}
